import { useEffect } from 'react';

/**
 * Head-meta kezelés React 19 alatt — a meglévő tageket FRISSÍTI, nem újakat szúr be.
 *
 * Miért nem react-helmet(-async)? A React 19 a fában renderelt <meta>/<link>/<title>
 * elemeket automatikusan a <head>-be emeli, de az index.html statikus tagjeit nem
 * ismeri → minden meta duplán jelent meg (két canonical, két robots…). A statikus
 * tagek viszont kellenek: a JS-t nem futtató crawlerek (Facebook, LinkedIn, egyes
 * AI-botok) csak azt látják. Ezért a helyes minta: statikus alapértelmezés az
 * index.html-ben + kliens-oldali in-place frissítés nyelvváltáskor / aloldalon.
 */
export type SeoMetaSpec = {
  title?: string;
  /** `<html lang>` */
  lang?: string;
  /** `name`/`property` → content. `og:*` → property, minden más → name. */
  meta?: Record<string, string>;
  /** string → beállítja; null → eltávolítja (pl. noindex oldalon ne legyen canonical). */
  canonical?: string | null;
};

const upsert = (selector: string, create: () => HTMLElement, apply: (el: HTMLElement) => void) => {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  apply(el);
};

export function useSeoMeta(spec: SeoMetaSpec) {
  const key = JSON.stringify(spec);

  useEffect(() => {
    const s: SeoMetaSpec = JSON.parse(key);

    if (s.title) document.title = s.title;
    if (s.lang) document.documentElement.lang = s.lang;

    for (const [name, content] of Object.entries(s.meta ?? {})) {
      const attr = name.startsWith('og:') ? 'property' : 'name';
      upsert(
        `meta[${attr}="${name}"]`,
        () => {
          const m = document.createElement('meta');
          m.setAttribute(attr, name);
          return m;
        },
        (el) => el.setAttribute('content', content),
      );
    }

    if (s.canonical === null) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
    } else if (typeof s.canonical === 'string') {
      const href = s.canonical;
      upsert(
        'link[rel="canonical"]',
        () => {
          const l = document.createElement('link');
          l.setAttribute('rel', 'canonical');
          return l;
        },
        (el) => el.setAttribute('href', href),
      );
    }
  }, [key]);
}
